import { useCallback, useEffect, useState } from 'react';

/*
 * usePwaInstall
 *
 * Cross-platform install hook.
 *
 * On Chromium browsers (desktop Chrome/Edge + Android Chrome) the browser
 * fires `beforeinstallprompt` when the app is installable. We capture that
 * event so we can present our own install CTA later via `promptInstall()`.
 *
 * iOS Safari does NOT fire `beforeinstallprompt`. To install on iOS users
 * must tap Share → "Add to Home Screen". We detect iOS and report it so the
 * UI can show a short instructional tooltip instead.
 *
 * Dismissal is persisted in localStorage so we don't nag users who already
 * said "not now". Re-installation surfaces are still available in Profile.
 */
const LS_DISMISSED_KEY = 'groovlens:pwa-banner-dismissed';

function _isStandalone() {
  if (typeof window === 'undefined') return false;
  // iOS
  if (window.navigator && window.navigator.standalone === true) return true;
  // Everyone else
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch (_) {
    return false;
  }
}

function _isIos() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  // iPadOS reports as Mac with touch; detect that case too.
  const iOS = /iPhone|iPad|iPod/i.test(ua);
  const iPadOS = /Macintosh/i.test(ua) && 'ontouchend' in window.document;
  return iOS || iPadOS;
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(_isStandalone());
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem(LS_DISMISSED_KEY) === '1';
    } catch (_) {
      return false;
    }
  });

  // Capture the install prompt when the browser offers it.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { outcome: 'unavailable' };
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      // We can only prompt once — drop the saved event regardless of choice.
      setDeferredPrompt(null);
      return choice; // { outcome: 'accepted' | 'dismissed', platform: ... }
    } catch (_) {
      setDeferredPrompt(null);
      return { outcome: 'error' };
    }
  }, [deferredPrompt]);

  const dismissBanner = useCallback(() => {
    try { localStorage.setItem(LS_DISMISSED_KEY, '1'); } catch (_) { /* localStorage may be unavailable in private mode */ }
    setBannerDismissed(true);
  }, []);

  const resetBannerDismissal = useCallback(() => {
    try { localStorage.removeItem(LS_DISMISSED_KEY); } catch (_) { /* localStorage may be unavailable in private mode */ }
    setBannerDismissed(false);
  }, []);

  const isIos = _isIos();
  const isAndroidChromium = (() => {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent || '';
    return /Android/i.test(ua) && /Chrome|Edg/i.test(ua) && !/SamsungBrowser|Firefox|OPR|Brave/i.test(ua);
  })();
  const isDesktopChromium = (() => {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent || '';
    return !/Mobile|Android|iPhone|iPad|iPod/i.test(ua) && /Chrome|Edg/i.test(ua);
  })();
  const isFirefoxMobile = (() => {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent || '';
    // Firefox on Android uses "Firefox/" in UA. Firefox on iOS uses "FxiOS/".
    return /Mobile|Android|iPhone|iPad|iPod/i.test(ua) && /Firefox|FxiOS/i.test(ua);
  })();
  const isSamsungBrowser = (() => {
    if (typeof window === 'undefined') return false;
    return /SamsungBrowser/i.test(window.navigator.userAgent || '');
  })();
  const isOperaMobile = (() => {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent || '';
    return /Mobile|Android/i.test(ua) && /OPR|Opera/i.test(ua);
  })();
  // Catch-all for any mobile browser we haven't specifically detected
  // (Brave, DuckDuckGo, Edge mobile non-Chromium, in-app webviews, etc.)
  const isMobile = (() => {
    if (typeof window === 'undefined') return false;
    return /Mobile|Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent || '');
  })();

  // The banner is shown when:
  //   - app is not already installed, AND
  //   - the user hasn't dismissed it, AND
  //   - either we have a captured prompt (Chromium) OR we recognise the
  //     platform / browser well enough to show useful instructions.
  // On mobile we ALWAYS show the banner (with fallback instructions if
  // nothing more specific matches) so users on Firefox / Brave / Samsung /
  // Opera / etc. don't get a dead end.
  const canShowBanner =
    !installed &&
    !bannerDismissed &&
    (!!deferredPrompt || isIos || isAndroidChromium || isDesktopChromium || isMobile);

  // The Profile install card is shown whenever the app is not yet installed.
  // The button text/behavior inside the card adapts to what the browser
  // actually supports (native prompt vs. iOS manual flow vs. generic
  // browser-specific instructions for everyone else).
  const canShowProfileInstall = !installed;

  return {
    installed,
    isIos,
    isAndroidChromium,
    isDesktopChromium,
    isFirefoxMobile,
    isSamsungBrowser,
    isOperaMobile,
    isMobile,
    canShowBanner,
    canShowProfileInstall,
    promptInstall,
    dismissBanner,
    resetBannerDismissal,
    hasNativePrompt: !!deferredPrompt,
  };
}

export default usePwaInstall;
